import { useRef } from 'react'
import { useIntl } from 'react-intl'
import { Meeting } from '../../../interfaces'
import { KTSVG } from '../../../_metronic/helpers'
import { approvalMeeting } from '../../../api/MeetingsCRUD'
import useAccessToken from '../../../hooks/AccessToken'
import Swal from 'sweetalert2'
import useHandleCloseModal from '../../../hooks/HandleCloseModal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment-timezone'
import { useDataTableStore } from '../../../stores/DataTableStore'
import { getDataById } from '../../../functions/general'
import { useMerchantStore } from '../../../stores/MerchantStore'
import { usePipelineStore } from '../../../stores/PipelineStore'
import { useMeetingStore } from '../../../stores/MeetingsStore'
import { useUserStore } from '../../../stores/UserStore'
import { useProductStore } from '../../../stores/ProductStore'

interface ModalApprovalMeetingProps {
    meeting: Meeting
    type: 'approve' | 'reject'
    customFields: any
}

const ModalApprovalMeeting = ({ meeting, type, customFields, }: ModalApprovalMeetingProps) => {
    const intl = useIntl()
    const token = useAccessToken()
    const modalRef = useRef<HTMLDivElement>(null)
    const closeModalRef = useRef<HTMLButtonElement>(null)
    const { tablesData, setTablesData } = useDataTableStore()
    const { simpleMerchants } = useMerchantStore()
    const { simplePipelines } = usePipelineStore()
    const { simpleMeetings } = useMeetingStore()
    const { users } = useUserStore()
    const { simpleProducts } = useProductStore()

    const formik = useFormik({
        initialValues: {
            approvalReason: '',
        },
        validationSchema: Yup.object().shape({
            approvalReason: type === 'approve' ? Yup.string().notRequired() : Yup.string().required(intl.formatMessage({ id: 'FORM.ACTION.APPROVAL_REASON_REQUIRED' })),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {

                const customFieldsValues: Record<string, string | string[] | null> = Object.keys(customFields).reduce(
                    (obj, item) => ({
                        ...obj,
                        [item]: (values as Record<string, string | string[] | null>)[item],
                    }),
                    {}
                );

                const convertCustomFields: Record<string, any> = Object.keys(customFields).reduce(
                    (obj, item) => {
                        if (!obj.info_custom_fields) {
                            obj.info_custom_fields = {};
                        }

                        if (customFields[item].type === 'merchant') {
                            const selectedMerchants = getDataById(values[item as keyof typeof values] as any, simpleMerchants);
                            obj[`custom_fields.${item}`] = selectedMerchants?.map((merchant: any) => merchant.name).join(", ");
                            obj.info_custom_fields[item] = selectedMerchants?.map((merchant: any) => ({
                                merchant_template_id: merchant.merchantTemplateId,
                                merchant_id: merchant.id,
                                merchant_name: merchant.name
                            })) || [];
                        }
                        else if (customFields[item].type === 'user') {
                            const selectedUsers = getDataById(values[item as keyof typeof values] as any, users);
                            obj[`custom_fields.${item}`] = selectedUsers?.map((user: any) => user.name).join(", ");
                            obj.info_custom_fields[item] = selectedUsers?.map((user: any) => ({
                                id: user.id,
                                name: user.name
                            })) || [];
                        }
                        else if (customFields[item].type === 'pipeline') {
                            const selectedPipeline = getDataById(values[item as keyof typeof values] as string, simplePipelines);
                            obj[`custom_fields.${item}`] = selectedPipeline ?
                                `${selectedPipeline.name} / ${selectedPipeline.nominal} / ${moment(selectedPipeline.dateCreated).format('DD MMM YYYY')}`
                                : '-';
                            obj.info_custom_fields[item] = selectedPipeline ? [{
                                id: selectedPipeline.id,
                                name: selectedPipeline.name,
                                nominal: selectedPipeline.nominal,
                                dateCreated: selectedPipeline.dateCreated
                            }] : [];
                        }
                        else if (customFields[item].type === 'meeting') {
                            const selectedMeeting: any[] = getDataById(values[item as keyof typeof values] as string, simpleMeetings);
                            obj[`custom_fields.${item}`] = selectedMeeting && selectedMeeting.length > 0 ?
                                selectedMeeting.map((meeting: any) => `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus}`).join(', ')
                                : '-';
                            obj.info_custom_fields[item] = selectedMeeting && selectedMeeting.length > 0 ? selectedMeeting.map((meeting: any) => ({
                                meeting_template_id: meeting.meetingTemplateId,
                                id: meeting.id,
                                merchant_name: meeting.name,
                                date_meet: meeting.dateMeet,
                                meeting_status: meeting.meetingStatus
                            })) : [];
                        } else if (customFields[item].type === 'product') {
                            const selectedProducts = getDataById(values[item as keyof typeof values] as any, simpleProducts);
                            obj[`custom_fields.${item}`] = selectedProducts?.map((product: any) => product.name).join(", ");
                            obj.info_custom_fields[item] = selectedProducts?.map((product: any) => ({
                                product_id: product.id,
                                product_name: product.name,
                                product_prices: product.product_prices,
                            })) || [];
                        }
                        else {
                            obj[`custom_fields.${item}`] = values[item as keyof typeof values] || '';
                            obj.info_custom_fields[item] = [];
                        }

                        return obj;
                    },
                    {} as Record<string, any>
                );

                const response = await approvalMeeting({
                    idMeeting: meeting.id,
                    approvalStatus: type === 'approve' ? "APPROVED" : "REJECTED",
                    approvalReason: values.approvalReason,
                    sendNotification: false,
                    token: token
                })
                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        title: `Berhasil ${type === 'approve' ? 'menyetujui' : 'menolak'} meeting`,
                        confirmButtonText: 'Ok',
                        target: document.getElementById('approval-meeting-modal')
                    })
                    closeModalRef.current?.click()
                    setTablesData('meetings', (tablesData.meetings.map((item) => {
                        if (item.id === meeting.id) {
                            return {
                                ...item,
                                ...response.data.data,
                                ...convertCustomFields['custom_fields'],
                                approval: {
                                    status: type === 'approve' ? intl.formatMessage({ id: 'MEETING.STATUS.APPROVED' }) : intl.formatMessage({ id: 'MEETING.STATUS.REJECTED' }),
                                    reason: values?.approvalReason || ''
                                },
                                meeting_status: type === 'approve' ? intl.formatMessage({ id: 'MEETING.STATUS.APPROVED' }) : intl.formatMessage({ id: 'MEETING.STATUS.REJECTED' }),
                                reminderMinutes: null,
                                info_custom_fields: convertCustomFields.info_custom_fields,
                                customFields: customFieldsValues,
                            }
                        }
                        return item
                    })))
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Gagal',
                        text: response.data.message || `Gagal ${type === 'approve' ? 'menyetujui' : 'menolak'} meeting`,
                        confirmButtonText: 'Ok',
                        target: document.getElementById(`${type}-meeting-modal-${meeting.id}`)
                    })
                }
            } catch (error: any) {
                console.log(error)
                Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error.response.data.message || `Gagal ${type === 'approve' ? 'menyetujui' : 'menolak'} meeting`,
                    confirmButtonText: 'Ok',
                    target: document.getElementById(`${type}-meeting-modal-${meeting.id}`)
                })
            } finally {
                setSubmitting(false)
            }
        },
    })

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <div className='modal fade' tabIndex={-1} id={`${type}-meeting-modal-${meeting.id}`} ref={modalRef}>
            <div className='modal-dialog modal-dialog-centered'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.APPROVE' })} {meeting.name}</h5>
                        <div
                            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                            onClick={handleCloseModal}
                        >
                            <KTSVG
                                path="/media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className='modal-body' style={{ minHeight: '250px' }}>
                            {meeting.latestVisitWithMerchant && (
                                <div className='row mb-5'>
                                    <div className='col-md-12'>
                                        <div className='card shadow-sm'>
                                            <div className='card-header bg-light'>
                                                <h3 className='card-title fw-bold fs-5 mb-0'>
                                                    <i className='fas fa-history me-2'></i>
                                                    Informasi Kunjungan Terakhir
                                                </h3>
                                            </div>
                                            <div className='card-body'>
                                                <div className='d-flex flex-column'>
                                                    <div className='d-flex align-items-center mb-3'>
                                                        <span className='fw-bold text-dark me-2 min-w-125px'>Nama Merchant:</span>
                                                        <span className='text-gray-800'>{meeting.latestVisitWithMerchant.merchantName || '-'}</span>
                                                    </div>
                                                    <div className='d-flex align-items-center mb-3'>
                                                        <span className='fw-bold text-dark me-2 min-w-125px'>Sales yang Visit:</span>
                                                        <span className='text-gray-800'>{meeting.latestVisitWithMerchant.ownerName || '-'}</span>
                                                    </div>
                                                    <div className='d-flex align-items-center'>
                                                        <span className='fw-bold text-dark me-2 min-w-125px'>Tanggal & Jam Visit:</span>
                                                        <span className='text-gray-800'>
                                                            {(meeting.latestVisitWithMerchant.dateVisitIn && moment(meeting.latestVisitWithMerchant.dateVisitIn).format('DD MMM YYYY HH:mm')) || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='form-group'>
                                        <label className={`form-label fs-6 fw-bolder text-dark ${type === 'approve' ? '' : 'required'}`} htmlFor='approvalReason'>{intl.formatMessage({ id: type === 'approve' ? 'FORM.ACTION.APPROVAL_REASON' : 'FORM.ACTION.REJECT_REASON' })} {type === 'approve' ? '(Opsional)' : ''}</label>
                                        <textarea
                                            className={`form-control${formik.touched.approvalReason && formik.errors.approvalReason ? ' is-invalid' : ''}`}
                                            id='approvalReason'
                                            name='approvalReason'
                                            placeholder={intl.formatMessage({ id: type === 'approve' ? 'FORM.ACTION.APPROVAL_REASON' : 'FORM.ACTION.REJECT_REASON' })}
                                            value={formik.values.approvalReason}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={3}
                                        ></textarea>
                                        {formik.touched.approvalReason && formik.errors.approvalReason && type === 'reject' && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className='text-danger'>{formik.errors.approvalReason}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='modal-footer'>
                            <button
                                type="button"
                                className="btn btn-light d-none"
                                data-bs-dismiss="modal"
                                ref={closeModalRef}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleCloseModal}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button
                                className={`btn btn-${type === 'approve' ? 'success' : 'danger'} skeleton skeleton-button ${formik.isSubmitting ? 'disabled' : ""}`}
                                data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}
                                type="submit"
                                disabled={formik.isSubmitting}
                            >
                                <i className={`fas ${type === 'approve' ? 'fa-check' : 'fa-times'} fs-2 me-2`}></i>
                                <span className="indicator-label">
                                    {intl.formatMessage({ id: type === 'approve' ? 'FORM.ACTION.APPROVE' : 'FORM.ACTION.REJECT' })}
                                </span>
                                <span className="indicator-progress">
                                    Loading... &nbsp;
                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default ModalApprovalMeeting